<template>
  <div>
    <v-app-bar
      dense
      flat
      height="50"
    >
      <v-row align="center">
        <v-col
          xs="12"
          sm="6"
          md="6"
          class="d-none d-sm-flex align-center"
        >
          <div class="mr-1">
            <v-text-field
              v-model.trim="searchVal"
              append-icon="mdi-magnify"
              placeholder="Search version"
              single-line
              clearable
              outlined
              hide-details
              dense
              @input="onVersionChange(searchVal)"
            />
          </div>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                id="app_type"
                color="primary"
                :disabled="loader"
                class="mr-1 elevation-0"
                v-bind="attrs"
                v-on="on"
              >
                {{ selectedAppType }}
                <v-icon right>
                  mdi-menu-down
                </v-icon>
              </v-btn>
            </template>
            <v-list class="py-0">
              <div
                v-for="appType in appTypes"
                :key="`template-appType--${appType}`"
              >
                <v-list-item @click="selectedAppType = appType; getDigitoryApps()">
                  {{ appType }}
                </v-list-item>
                <v-divider />
              </div>
            </v-list>
          </v-menu>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                id="app_type"
                color="primary"
                :disabled="loader"
                class="mr-1 elevation-0"
                v-bind="attrs"
                v-on="on"
              >
                {{ selectedPlatform }}
                <v-icon right>
                  mdi-menu-down
                </v-icon>
              </v-btn>
            </template>
            <v-list class="py-0">
              <div
                v-for="platform in platforms"
                :key="`template-platform--${platform}`"
              >
                <v-list-item @click="selectedPlatform = platform; getDigitoryApps()">
                  {{ platform }}
                </v-list-item>
                <v-divider />
              </div>
            </v-list>
          </v-menu>
        </v-col>
        <v-col
          xs="12"
          sm="6"
          align="end"
          class="d-none d-flex justify-end align-center"
        >
          <v-btn
            id="app_refresh"
            color="primary"
            :loading="loader"
            class="mr-1 elevation-0"
            @click.native="reload()"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <v-btn
            id="new_app"
            class="mr-1 elevation-0"
            color="success"
            @click="newApp()"
          >
            <v-icon>mdi-plus</v-icon> App
          </v-btn>
          <v-btn
            id="new_app_upload"
            class="elevation-0"
            color="success"
            @click="upload()"
          >
            <v-icon left>
              mdi-plus
            </v-icon>
            Upload
          </v-btn>
        </v-col>
      </v-row>
    </v-app-bar>
    <v-card
      flat
      tile
    >
      <v-card-text>
        <v-row>
          <v-col
            xs="12"
            class="pa-0"
          >
            <v-data-table
              :headers="headers"
              :items="apps"
              :items-per-page="limit"
              :loading="loader"
              hide-default-footer
              mobile-breakpoint="0"
            >
              <template v-slot:[`item.git_ref`]="{ item }">
                <span v-if="item.git_ref">
                  {{ item.git_ref.branch }}
                  <span v-if="item.git_ref.commit_id">@{{ item.git_ref.commit_id }}</span>
                  <span v-if="item.git_ref.tag">(tag: {{ item.git_ref.tag }})</span>
                </span>
              </template>
              <template v-slot:[`item.file_path`]="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <a
                      :href="item.file_path"
                      target="_blank"
                      v-on="on"
                    >
                      Download
                    </a>
                  </template>
                  <span>{{ item.file_path }}</span>
                </v-tooltip>
                <copy-button :text="item.file_path" />
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-icon
                  color="error"
                  small
                  @click="deleteDigitoryApp(item.id)"
                >
                  mdi-delete
                </v-icon>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions v-show="totalPage > 1">
        <v-row>
          <v-col>
            <v-pagination
              v-model="page"
              :length="totalPage"
              @input="getDigitoryApps()"
            />
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
    <new-digitory-app
      ref="digitoryAppDialog"
      @refresh="reload"
    />
    <upload-app
      ref="uploadAppDialog"
      @refresh="reload"
    />
  </div>
</template>

<script>
import { failure, pagination } from "@/mixins/common";
import NewDigitoryApp from "./NewDigitoryApp.vue";
import UploadApp from "./UploadApp.vue";
import CopyButton from "@/components/utils/CopyButton.vue";

export default {
  name: "DigitoryApps",
  components: {
    NewDigitoryApp,
    UploadApp,
    CopyButton
  },
  mixins: [failure, pagination],
  data() {
    return {
      loader: false,
      apps: [],
      headers: [
        { text: "App Type", value: "app_type", sortable: false },
        { text: "Platform", value: "platform", align: "center", sortable: false },
        { text: "Version", value: "version", align: "right", sortable: false },
        { text: "GitRef", value: "git_ref", align: "left", sortable: false },
        { text: "Link", value: "file_path", align: "center", sortable: false },
        { text: "Actions", value: "actions", align: "right", sortable: false }
      ],
      sortKey: "ordinal",
      sortOrder: "DESC",
      searchKey: "version",
      page: 1,
      totalPage: 0,
      selectedAppType: "All Apps",
      appTypes: ["All Apps", "pos_engine", "pos", "kds", "kiosk"],
      selectedPlatform: "All Platforms",
      platforms: ["All Platforms", "android", "windows", "mac", "ios", "linux"],
      posVersion: "",
      searchVal: ""
    };
  },
  created() {
    this.init();
  },
  methods: {
    newApp() {
      this.$refs.digitoryAppDialog.open();
    },
    upload() {
      this.$refs.uploadAppDialog.open();
    },
    getDigitoryApps() {

      this.loader = true;
      this.apps = [];
      let url = `${this.ADMIN_API_URL}/digitoryApps?page=${this.page - 1}&search_key=${this.searchKey}&sort_key=${this.sortKey}&sort_order=${this.sortOrder}&limit=${this.limit}&status=${this.status}`;
      if (this.posVersion) {
        url += `&pos_version=${this.posVersion}`;
      }
      if (this.selectedPlatform !== "All Platforms") {
        url += `&platform=${this.selectedPlatform}`;
      }
      if (this.selectedAppType !== "All Apps") {
        url += `&app_type=${this.selectedAppType}`;
      }
      this.$http.get(url).then(
        function (res) {
          this.apps = res.body.data || [];
          this.totalPage = res.body.totalPage;
          this.page = res.body.pagination.page + 1;
          this.loader = false;
        },
        function (failure) {
          this.loader = false;
          this.handleFailure(failure);
        }
      );
    },
    deleteDigitoryApp(id) {
      this.$root.$confirm
        .open("Confirm", "Are you sure you want to delete?", {})
        .then((confirm) => {
          if (!confirm) {
            return;
          }
          this.loader = true;
          this.$http.delete(`${this.ADMIN_API_URL}/digitoryApps/${id}`).then(
            (data) => {
              this.getDigitoryApps();
              this.loader = false;
              this.handleSuccess(data.body);
            },
            (failure) => {
              this.loader = false;
              this.handleFailure(failure);
            }
          );
        });
    },
    search() {
      this.page = 1;
      this.getDigitoryApps();
    },
    onVersionChange(val) {
      this.posVersion = val;
      if (!val || val.length >= 4) {
        this.search();
      }
    },
    reload() {
      this.posVersion = "";
      this.selectedAppType = "All Apps";
      this.selectedPlatform = "All Platforms";
      this.search();
    },
    init: function () {
      this.$store.commit("setTitle", "Digitory Apps");
      this.getDigitoryApps();
    },
    handleSuccess(response) {
      this.$store.commit("showSnackbar", {
        timeout: 3000,
        message: response.message
      });
    }
   
  }
};
</script>
